import { useParams } from "@tanstack/react-router";
import { useServiceId } from "api/hooks/useServiceId";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PersistStorage, StorageValue } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { debounce } from "es-toolkit";
import { z } from "zod/v4";
import { closeSqlWidgetSession } from "utils/sqlWidgetSessions";
import {
  EXPLORER_STATE,
  getFromStorage,
  LOCAL_STORAGE_USER,
  removeFromStorage,
  STORAGE_TYPE,
  storeDirectlyIntoStorage,
} from "utils/localStorage";

const explorerObjectTabSchema = z.object({
  type: z.literal("object"),
  schema: z.string(),
  objectType: z.string(),
  objectName: z.string(),
});

/**
 * An open query. The tab only references the query: its name and text live
 * in the query files store (see `persistentState.ts`).
 */
const explorerQueryTabSchema = z.object({
  type: z.literal("query"),
  /** The id of the query node, which also identifies the query in the route. */
  id: z.string(),
});

const explorerTabSchema = z.discriminatedUnion("type", [
  explorerObjectTabSchema,
  explorerQueryTabSchema,
]);

export type ExplorerObjectTab = z.infer<typeof explorerObjectTabSchema>;
export type ExplorerQueryTab = z.infer<typeof explorerQueryTabSchema>;
export type ExplorerTab = z.infer<typeof explorerTabSchema>;

export const serviceKey = (projectId: string, serviceId: string) =>
  `${projectId}/${serviceId}`;

/** The identity of a tab within its service. */
const tabIdentity = (tab: ExplorerTab): string => {
  switch (tab.type) {
    case "object":
      return `object:${tab.schema}.${tab.objectType}.${tab.objectName}`;
    case "query":
      return `query:${tab.id}`;
  }
};

/**
 * The key of a tab in `tabsByKey`: the service key, then the identity of the
 * tab. Two tabs with the same key are the same tab.
 */
export const tabKey = (
  projectId: string,
  serviceId: string,
  tab: ExplorerTab,
): string => `${serviceKey(projectId, serviceId)}/${tabIdentity(tab)}`;

export const queryTabKey = (
  projectId: string,
  serviceId: string,
  queryId: string,
) => `${serviceKey(projectId, serviceId)}/query:${queryId}`;

/** The query id in a query tab key, see `queryTabKey`. */
const queryIdOfTabKey = (key: string) => key.match(/\/query:([^/]+)$/)?.[1];

/**
 * Each query tab runs in its own database session. The key is the tab key,
 * not the query id: one query can be open in more than one service.
 */
export const explorerQuerySessionKey = (queryTabKey: string) =>
  `explorerQueryWidget:${queryTabKey}`;

/**
 * The tab order and the selection are kept per service, by `serviceKey`. The
 * tabs themselves live in one flat map by `tabKey`, so a component can
 * subscribe to one tab and an edit to a tab does not touch the order array.
 */
const explorerPersistedStateSchema = z.object({
  /**
   * The user whose tabs these are, see `claimExplorerSession`. `null` until a
   * user claims the state.
   */
  userId: z.string().nullable(),
  /** The keys of the open tabs of each service, in display order. */
  tabs: z.partialRecord(z.string(), z.array(z.string())),
  tabsByKey: z.partialRecord(z.string(), explorerTabSchema),
  selectedTabs: z.partialRecord(z.string(), z.string()),
  /** The ids of the expanded folders of the query files tree. */
  expandedFolders: z.partialRecord(z.string(), z.literal(true)),
  /** The view of the sidebar: the schema tree, or the saved queries. */
  sidebarView: z.enum(["schemas", "saved-queries"]),
});

export type ExplorerSidebarView = ExplorerPersistedState["sidebarView"];

/** The zustand persist envelope, as written by `setItem`. */
const storedExplorerStateSchema = z.object({
  state: explorerPersistedStateSchema,
  version: z.number().optional(),
});

type ExplorerPersistedState = z.infer<typeof explorerPersistedStateSchema>;

interface ExplorerState extends ExplorerPersistedState {
  /**
   * The latest run of each query tab, keyed by the query id. Not persisted:
   * the results live in the query widget's in-memory cache, which a reload
   * discards.
   */
  queryRunIds: Partial<Record<string, string>>;
}

const EMPTY_STATE: ExplorerState = {
  userId: null,
  tabs: {},
  tabsByKey: {},
  selectedTabs: {},
  expandedFolders: {},
  sidebarView: "schemas" as const,
  queryRunIds: {},
};

/**
 * Removes the given tabs of one service from a draft of the state. The home
 * tab is selected when the selected tab is one of them. Unknown keys are
 * ignored; when none is open, the draft is left untouched so nothing
 * re-renders. The tabs in `keptRunKeys` keep their latest run.
 */
const closeTabsInDraft = (
  draft: ExplorerState,
  projectId: string,
  serviceId: string,
  keys: string[],
  keptRunKeys?: Set<string>,
) => {
  const service = serviceKey(projectId, serviceId);
  const closing = new Set(keys.filter((key) => draft.tabsByKey[key]));
  if (closing.size === 0) return;
  for (const key of closing) {
    if (!keptRunKeys?.has(key)) delete draft.queryRunIds[key];
    delete draft.tabsByKey[key];
  }
  draft.tabs[service] = (draft.tabs[service] ?? []).filter(
    (openKey) => !closing.has(openKey),
  );
  const selected = draft.selectedTabs[service];
  if (selected && closing.has(selected)) {
    draft.selectedTabs[service] = "home";
  }
};

const persistExplorerState = debounce(
  (name: string, value: StorageValue<ExplorerPersistedState>) => {
    storeDirectlyIntoStorage(
      name,
      value,
      LOCAL_STORAGE_USER,
      STORAGE_TYPE.SESSION,
    );
  },
  400,
);

// A pending write would be lost when the page goes away, so write it now.
// `flush` does nothing when no write is pending.
window.addEventListener("pagehide", () => persistExplorerState.flush());

/**
 * Adapts the shared storage helpers to the zustand persist middleware. The
 * helpers already serialize to JSON, so this implements `PersistStorage`
 * directly instead of wrapping a string-based `StateStorage` in
 * `createJSONStorage`, which would double-encode the value.
 *
 * `name` is the `type` field inside the `LOCAL_STORAGE_USER` session bucket.
 * A stored value that does not match the schema (an older shape, or a manual
 * edit) is logged and dropped, so the store starts from its default.
 */
const explorerStorage: PersistStorage<ExplorerPersistedState> = {
  getItem: (name) => {
    const stored = getFromStorage<unknown>(
      name,
      LOCAL_STORAGE_USER,
      null,
      STORAGE_TYPE.SESSION,
    );
    if (stored === null) return null;
    const result = storedExplorerStateSchema.safeParse(stored);
    if (!result.success) {
      console.error(
        `Ignoring invalid "${name}" in storage key "${LOCAL_STORAGE_USER}":`,
        z.prettifyError(result.error),
      );
      return null;
    }
    return result.data;
  },
  setItem: persistExplorerState,
  removeItem: (name) => {
    persistExplorerState.cancel();
    removeFromStorage(name, LOCAL_STORAGE_USER, STORAGE_TYPE.SESSION);
  },
};

/**
 * The tabs and the sidebar of the explorer. The actions are the exported
 * functions below, not fields of the store: they never change, so a component
 * calls them directly and does not subscribe to them.
 */
export const useExplorerSessionState = create<ExplorerState>()(
  persist(
    immer<ExplorerState>(() => EMPTY_STATE),
    {
      name: EXPLORER_STATE,
      storage: explorerStorage,
      partialize: ({
        userId,
        tabs,
        tabsByKey,
        selectedTabs,
        expandedFolders,
        sidebarView,
      }): ExplorerPersistedState => ({
        userId,
        tabs,
        tabsByKey,
        selectedTabs,
        expandedFolders,
        sidebarView,
      }),
    },
  ),
);

const { setState: set } = useExplorerSessionState;

export const resetExplorerState = () => {
  set(EMPTY_STATE);
  // Clear storage after set to cancel the debounced reset write too.
  useExplorerSessionState.persist.clearStorage();
};

/**
 * Makes the state the state of the user. Signing out in another browser tab
 * does not clear the session storage of this one, so after a sign-in as
 * another user this tab still has the tabs of the previous one. They are
 * dropped: their queries belong to the previous user.
 */
export const claimExplorerSession = (userId: string) => {
  const owner = useExplorerSessionState.getState().userId;
  if (owner === userId) return;
  if (owner !== null) resetExplorerState();
  set((draft) => {
    draft.userId = userId;
  });
};

export const openTab = (
  projectId: string,
  serviceId: string,
  tab: ExplorerTab,
) =>
  set((draft) => {
    const key = tabKey(projectId, serviceId, tab);
    // An untouched draft leaves the state as is, so nothing re-renders.
    if (draft.tabsByKey[key]) return;
    draft.tabsByKey[key] = tab;
    (draft.tabs[serviceKey(projectId, serviceId)] ??= []).push(key);
  });

export const closeTab = (projectId: string, serviceId: string, key: string) =>
  set((draft) => closeTabsInDraft(draft, projectId, serviceId, [key]));

/**
 * Closes several tabs of one service in a single update. The tabs in
 * `keptRunKeys` keep their latest run, such as the tab of the scratchpad,
 * whose run the floating SQL editor still shows.
 */
export const closeTabs = (
  projectId: string,
  serviceId: string,
  keys: string[],
  keptRunKeys?: Set<string>,
) =>
  set((draft) =>
    closeTabsInDraft(draft, projectId, serviceId, keys, keptRunKeys),
  );

/**
 * Moves an open tab to `index` in the tab order of its service. An index past
 * the end moves the tab to the end.
 */
export const moveTab = (
  projectId: string,
  serviceId: string,
  key: string,
  index: number,
) =>
  set((draft) => {
    const order = draft.tabs[serviceKey(projectId, serviceId)];
    const from = order?.indexOf(key) ?? -1;
    if (!order || from === -1) return;
    const to = Math.max(0, Math.min(index, order.length - 1));
    if (from === to) return;
    order.splice(to, 0, ...order.splice(from, 1));
  });

export const selectTab = (projectId: string, serviceId: string, key: string) =>
  set((draft) => {
    draft.selectedTabs[serviceKey(projectId, serviceId)] = key;
  });

/**
 * Closes the database sessions and forgets the runs of the closed tabs of the
 * given queries that kept their run (see `closeTabs`), in every service. For
 * a query that is not the scratchpad any more.
 */
export const releaseKeptRuns = (queryIds: string[]) => {
  const ids = new Set(queryIds);
  const { tabsByKey, queryRunIds } = useExplorerSessionState.getState();
  const keptRunKeys = Object.keys(queryRunIds).filter((key) => {
    const queryId = queryIdOfTabKey(key);
    return !tabsByKey[key] && queryId !== undefined && ids.has(queryId);
  });
  if (keptRunKeys.length === 0) return;
  for (const key of keptRunKeys) {
    closeSqlWidgetSession(explorerQuerySessionKey(key), queryRunIds[key]);
  }
  set((draft) => {
    for (const key of keptRunKeys) delete draft.queryRunIds[key];
  });
};

/**
 * Closes the tabs of the given queries in every service, and their database
 * sessions, also the ones that closed tabs kept (see `releaseKeptRuns`). For
 * a query that is deleted.
 */
export const closeQueryTabsEverywhere = (queryIds: string[]) => {
  releaseKeptRuns(queryIds);
  const ids = new Set(queryIds);
  const { tabsByKey, queryRunIds } = useExplorerSessionState.getState();
  const isClosing = (key: string) => {
    const tab = tabsByKey[key];
    return tab?.type === "query" && ids.has(tab.id);
  };
  for (const key of Object.keys(tabsByKey).filter(isClosing)) {
    closeSqlWidgetSession(explorerQuerySessionKey(key), queryRunIds[key]);
  }
  set((draft) => {
    for (const [service, keys] of Object.entries(draft.tabs)) {
      const [projectId, serviceId] = service.split("/");
      closeTabsInDraft(
        draft,
        projectId,
        serviceId,
        (keys ?? []).filter(isClosing),
      );
    }
  });
};

export const setQueryRunId = (queryTabKey: string, runId: string) =>
  set((draft) => {
    draft.queryRunIds[queryTabKey] = runId;
  });

export const setFolderExpanded = (folderId: string, expanded: boolean) =>
  set((draft) => {
    if (expanded) draft.expandedFolders[folderId] = true;
    else delete draft.expandedFolders[folderId];
  });

export const setSidebarView = (view: ExplorerSidebarView) =>
  set((draft) => {
    draft.sidebarView = view;
  });

export const useExplorerRouteIds = () => {
  const projectId = useParams({
    strict: false,
    select: (params) => params.projectId ?? "",
  });
  // The service the Explorer reads, which is the branch inside the branch
  // area. Keying the tabs by the route param would give a branch and its
  // parent one set of tabs.
  const serviceId = useServiceId();
  return { projectId, serviceId, serviceKey: serviceKey(projectId, serviceId) };
};

/**
 * True when another open object tab of the current service has the same
 * object name, so the tab label must include the schema.
 */
export const useIsObjectNameShared = (tab: ExplorerObjectTab) => {
  const { projectId, serviceId } = useExplorerRouteIds();
  const service = serviceKey(projectId, serviceId);
  return useExplorerSessionState(
    (state) =>
      (state.tabs[service] ?? []).filter((key) => {
        const openTab = state.tabsByKey[key];
        return (
          openTab?.type === "object" && openTab.objectName === tab.objectName
        );
      }).length > 1,
  );
};

/**
 * Whether the query with the given id is open in the current service, with
 * the run state of its tab.
 */
export const useExplorerQueryTab = (queryId: string) => {
  const { projectId, serviceId } = useExplorerRouteIds();
  const key = queryTabKey(projectId, serviceId, queryId);
  const isOpen = useExplorerSessionState(
    (state) => state.tabsByKey[key]?.type === "query",
  );
  const runId = useExplorerSessionState(
    (state) => state.queryRunIds[key] ?? null,
  );
  return {
    isOpen,
    runId,
    setRunId: (newRunId: string) => setQueryRunId(key, newRunId),
    sessionKey: explorerQuerySessionKey(key),
  };
};

/**
 * The session keys of every open query tab, and of every closed query tab
 * that kept its run (see `closeTabs`), across all services.
 */
export const explorerQuerySessionKeys = () => {
  const { tabsByKey, queryRunIds } = useExplorerSessionState.getState();
  const queryTabKeys = new Set([
    ...Object.entries(tabsByKey).flatMap(([key, tab]) =>
      tab?.type === "query" ? [key] : [],
    ),
    ...Object.keys(queryRunIds),
  ]);
  return [...queryTabKeys].map(explorerQuerySessionKey);
};
